/**
* Responsibility:
* - Read cached rendered markup state for a form/signature pair.
*
* Touches:
* - Reads and validates entries from module-level form cache map.
*
* Guarantees:
* - Returns normalized `{ markup, noResults }` or `null`.
*/
export function readResultMarkupCache(cacheByForm, formId, signature) {
	if (!(cacheByForm instanceof Map)) {
		return null;
	}

	const formCache = cacheByForm.get(String(formId));
	if (!(formCache instanceof Map)) {
		return null;
	}

	const cached = formCache.get(String(signature || '').trim());
	if (!cached || typeof cached !== 'object') {
		return null;
	}

	const markup = typeof cached.markup === 'string' ? cached.markup.trim() : '';
	if (markup === '') {
		return null;
	}

	return {
		markup,
		noResults: cached.noResults === true,
	};
}

/**
* Responsibility:
* - Persist rendered markup state for a form/signature pair.
*
* Touches:
* - Mutates module-level form cache map by writing normalized cache entry.
*
* Guarantees:
* - Writes only when signature and markup are both non-empty.
*/
export function writeResultMarkupCache(cacheByForm, formId, signature, markup, noResults) {
	if (!(cacheByForm instanceof Map)) {
		return;
	}

	const normalizedSignature = String(signature || '').trim();
	const normalizedMarkup = typeof markup === 'string' ? markup.trim() : '';
	if (normalizedSignature === '' || normalizedMarkup === '') {
		return;
	}

	const key = String(formId);
	let formCache = cacheByForm.get(key);
	if (!(formCache instanceof Map)) {
		formCache = new Map();
		cacheByForm.set(key, formCache);
	}

	formCache.set(normalizedSignature, {
		markup: normalizedMarkup,
		noResults: noResults === true,
	});
}

/**
* Responsibility:
* - Clear all cached rendered markup entries for one form.
*
* Touches:
* - Deletes form-scoped cache map entry from module-level cache.
*
* Guarantees:
* - Form cache is empty after execution.
*/
export function clearResultMarkupCache(cacheByForm, formId) {
	if (!(cacheByForm instanceof Map)) {
		return;
	}

	cacheByForm.delete(String(formId));
}
